import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import EndpointEditor from '../components/EndpointEditor';
import EnvironmentsPanel from '../components/EnvironmentsPanel';
import SchemasPanel from '../components/SchemasPanel';
import TestRunsPanel from '../components/TestRunsPanel';
import MockPanel from '../components/MockPanel';
import CollectionRunner from '../components/CollectionRunner';
import DocsPanel from '../components/DocsPanel';
import PublishDocsPage from './PublishDocsPage';
import StatsPanel from '../components/StatsPanel';
import TicketsPanel from '../components/TicketsPanel';
import GlobalSearch from '../components/GlobalSearch';
import ImportModal from '../components/ImportModal';
import ProjectSettingsModal from '../components/ProjectSettingsModal';
import { Plus, Upload, Settings, Download } from 'lucide-react';

const METHODS = ['GET','POST','PUT','PATCH','DELETE','HEAD','OPTIONS'];
const METHOD_COLORS = { GET:'#34d399', POST:'#4f9cf9', PUT:'#fb923c', PATCH:'#a78bfa', DELETE:'#f87171', HEAD:'#4d6080', OPTIONS:'#4d6080' };

export default function ProjectPage() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [selectedEndpointId, setSelectedEndpointId] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [environments, setEnvironments] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [showNewEndpoint, setShowNewEndpoint] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newEpForm, setNewEpForm] = useState({ name:'', method:'GET', path:'/', folder_id:'' });
  const [folders, setFolders] = useState([]);
  const [sidebarRefresh, setSidebarRefresh] = useState(0);
  const [lastResponse, setLastResponse] = useState(null);

  const isClient = user?.role === 'client';
  const isAdmin  = ['admin','member'].includes(user?.role);

  useEffect(() => {
    loadProject();
    loadEnvironments();
    // Default view for client
    if (isClient) setActiveView('docs');
  }, [projectId]);

  useEffect(() => { loadFolders(); }, [projectId, sidebarRefresh]);

  async function loadProject() {
    try { const r = await api.get(`/projects/${projectId}`); setProject(r.data); }
    catch { navigate('/'); }
  }
  async function loadEnvironments() {
    try { const r = await api.get(`/projects/${projectId}/environments`); setEnvironments(r.data); } catch {}
  }
  async function loadFolders() {
    try { const r = await api.get(`/projects/${projectId}/endpoints`); setFolders(r.data.folders||[]); setEndpoints(r.data.endpoints||[]); } catch {}
  }

  async function createEndpoint(e) {
    e.preventDefault();
    try {
      const r = await api.post(`/projects/${projectId}/endpoints`, { name:newEpForm.name, method:newEpForm.method, path:newEpForm.path, folder_id:newEpForm.folder_id||null });
      toast.success('Endpoint created!');
      setShowNewEndpoint(false);
      setNewEpForm({ name:'', method:'GET', path:'/', folder_id:'' });
      setSidebarRefresh(n => n+1);
      setSelectedEndpointId(r.data.id); setSelectedEndpoint(r.data); setActiveView('endpoints');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function exportOpenAPI() {
    try {
      const r = await api.get(`/projects/${projectId}/export/openapi`);
      const blob = new Blob([JSON.stringify(r.data,null,2)], {type:'application/json'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href=url; a.download=`${project?.name||'api'}-openapi.json`; a.click();
      URL.revokeObjectURL(url); toast.success('Exported!');
    } catch { toast.error('Export failed'); }
  }

  function handleEndpointSelect(ep, viewOverride) {
    if (viewOverride) { setActiveView(viewOverride); setSelectedEndpointId(null); setSelectedEndpoint(null); }
    else if (ep) { setSelectedEndpointId(ep.id); setSelectedEndpoint(ep); setActiveView('endpoints'); }
  }

  function handleSearchSelect(item) {
    if (item._type === 'endpoint') { setSelectedEndpointId(item.id); setSelectedEndpoint(item); setActiveView('endpoints'); }
    else if (item._type === 'schema') setActiveView('schemas');
    else if (item._type === 'env') setActiveView('environments');
  }

  return (
    <div className="app-layout">
      <Sidebar projectId={projectId} onEndpointSelect={handleEndpointSelect}
        selectedEndpointId={selectedEndpointId} onRefresh={sidebarRefresh} activeView={activeView} />

      <div className="main-content">
        {/* Header */}
        <div style={P.header}>
          <div style={P.headerLeft}>
            <h1 style={P.projName}>{project?.name || '...'}</h1>
            {project?.base_url && <span style={P.baseUrl} className="mono">{project.base_url}</span>}
          </div>
          <div style={P.headerCenter}>
            <GlobalSearch projectId={projectId} onSelect={handleSearchSelect} />
          </div>
          <div style={P.headerRight}>
            {activeView==='endpoints' && isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowNewEndpoint(true)}>
                <Plus size={13}/> New Endpoint
              </button>
            )}
            {isAdmin && <>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowImport(true)}><Upload size={13}/> Import</button>
              <button className="btn btn-ghost btn-sm" onClick={exportOpenAPI}><Download size={13}/> Export</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowSettings(true)}><Settings size={14}/></button>
            </>}
          </div>
        </div>

        {/* Main */}
        <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column' }}>
          {activeView==='overview'     && <StatsPanel projectId={projectId} project={project} />}
          {activeView==='endpoints'    && <EndpointEditor endpointId={selectedEndpointId} projectId={projectId} projectBaseUrl={project?.base_url} onSaved={() => setSidebarRefresh(n=>n+1)} environments={environments} onResponse={setLastResponse} />}
          {activeView==='environments' && <EnvironmentsPanel projectId={projectId} />}
          {activeView==='schemas'      && <SchemasPanel projectId={projectId} />}
          {activeView==='mock'         && <MockPanel endpoint={selectedEndpoint} projectId={projectId} onEndpointSelect={ep => { setSelectedEndpointId(ep.id); setSelectedEndpoint(ep); }} />}
          {activeView==='runner'       && <CollectionRunner projectId={projectId} environments={environments} />}
          {activeView==='docs'         && <DocsPanel projectId={projectId} project={project} />}
          {activeView==='publish'      && <PublishDocsPage projectId={projectId} project={project} />}
          {activeView==='tests'        && <TestRunsPanel projectId={projectId} />}
          {activeView==='tickets'      && <TicketsPanel projectId={projectId} endpoints={endpoints} environments={environments} lastResponse={lastResponse} />}
        </div>
      </div>

      {showNewEndpoint && (
        <div className="modal-overlay" onClick={() => setShowNewEndpoint(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">New API Endpoint</h3>
            <form onSubmit={createEndpoint}>
              <div className="form-group"><label className="form-label">Name *</label>
                <input placeholder="e.g. Get Users" value={newEpForm.name} onChange={e => setNewEpForm(f=>({...f,name:e.target.value}))} required autoFocus /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Method</label>
                  <select value={newEpForm.method} onChange={e => setNewEpForm(f=>({...f,method:e.target.value}))} style={{ color:METHOD_COLORS[newEpForm.method] }}>
                    {METHODS.map(m => <option key={m} value={m} style={{ color:METHOD_COLORS[m] }}>{m}</option>)}
                  </select></div>
                <div className="form-group"><label className="form-label">Folder</label>
                  <select value={newEpForm.folder_id} onChange={e => setNewEpForm(f=>({...f,folder_id:e.target.value}))}>
                    <option value="">No folder</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select></div>
              </div>
              <div className="form-group"><label className="form-label">Path *</label>
                <input placeholder="/users/{id}" value={newEpForm.path} onChange={e => setNewEpForm(f=>({...f,path:e.target.value}))} required className="mono" /></div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowNewEndpoint(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showImport && <ImportModal projectId={projectId} onClose={() => setShowImport(false)} onImported={() => { setSidebarRefresh(n=>n+1); setShowImport(false); }} />}
      {showSettings && project && <ProjectSettingsModal project={project} onClose={() => setShowSettings(false)} onUpdated={setProject} onDeleted={() => navigate('/')} />}
    </div>
  );
}

const P = {
  header: { display:'flex', alignItems:'center', gap:12, padding:'0 16px', borderBottom:'1px solid var(--border)', background:'var(--bg-surface)', minHeight:'var(--header-h)', flexShrink:0 },
  headerLeft: { flex:'0 0 auto', display:'flex', alignItems:'center', gap:10, maxWidth:260 },
  projName: { fontSize:14, fontWeight:800, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', letterSpacing:'-0.02em' },
  baseUrl: { fontSize:10, color:'var(--text-3)', background:'var(--bg-elevated)', padding:'2px 7px', borderRadius:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:160 },
  headerCenter: { flex:1, display:'flex', justifyContent:'center' },
  headerRight: { display:'flex', gap:5, flexShrink:0, alignItems:'center' },
};
